/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IActionContext } from '@msdyn365-commerce/core';
import { getDelayedDeliveryMsg } from '../../../../themes/dobbies/utilities/estDeliveryDate';

export interface IAdressInput {
    id?: string;
    name?: string;
    className: string;
    type: string;
    value: string;
    maxLength?: number;
    autoFocus?: boolean;
    additionalAddributes?: object;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    ctx?: IActionContext;
    productIds?: number[];
}

/**
 * Address input.
 * @param props - Configuration of the functional component.
 * @returns React functional component.
 */
export const AddressInputFunctionComponent: React.FC<IAdressInput> = (props: IAdressInput) => {
    const {
        id: itemId,
        name,
        className,
        type,
        value,
        maxLength,
        additionalAddributes,
        autoFocus: shouldUseAutoFocus,
        telemetryContent,
        onChange,
        ctx,
        productIds
    } = props;

    const payLoad = getPayloadObject('click', telemetryContent!, name!);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    /* VSI Customization - START - 03/02/21 - Mark input field as numeric field if it is for phone input */
    const isPhone = name ? name.toLowerCase() === 'phone' : undefined;
    const patternAttribute = isPhone
        ? { pattern: `^\s*\(?(020[7,8]{1}\)?[ ]?[1-9]{1}[0-9{2}[ ]?[0-9]{4})|(0[1-8]{1}[0-9]{3}\)?[ ]?[1-9]{1}[0-9]{2}[ ]?[0-9]{3})\s*$` }
        : {};

    const isZipCodeRestricted = (ctx: IActionContext, productId: number) => {
        const enteredZipCookie = ctx.requestContext.cookies.get('entered_zip_code');
        let enteredZipCode = '';
        if (enteredZipCookie.value) {
            enteredZipCode = enteredZipCookie.value as string;
        }
        const restrictedPostCodesInfoCookie = ctx.requestContext.cookies.get('restricted_post_codes_info');

        if (enteredZipCode) {
            const restrictedPostCodesInfo = restrictedPostCodesInfoCookie.value;
            if (Array.isArray(restrictedPostCodesInfo)) {
                const rpProductObj = restrictedPostCodesInfo.find(rpi => rpi['productId'] === productId);
                if (rpProductObj) {
                    const postCodes = rpProductObj['restrictedPostCodes'];
                    if (Array.isArray(postCodes)) {
                        const hasZipCode = postCodes.find(pc => enteredZipCode.startsWith(pc));
                        if (hasZipCode) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    };

    const getDelayedDelMsgForEnteredZipCode = (ctx: IActionContext, value?: string) => {
        const enteredZipCode = value || '';
        return getDelayedDeliveryMsg(ctx, enteredZipCode);
    };
    /* VSI Customization - END */
    return (
        <>
            <input
                name={name}
                id={itemId}
                className={`${className}__input ${className}__input-${type}`}
                type={isPhone ? 'number' : type} // VSI Customization to change input type to number for Phone
                {...patternAttribute} // VSI Customization to add just UK valid phone number
                autoFocus={shouldUseAutoFocus}
                value={value}
                maxLength={maxLength}
                {...(additionalAddributes || {})}
                {...attributes}
                onChange={onChange}
                inputMode={isPhone ? 'tel' : 'text'}
                placeholder={name === 'Street' ? 'Start typing your address...' : ''}
            />
            {name?.toLowerCase() === 'zipcode' &&
            ctx &&
            productIds?.length &&
            productIds?.some(prodId => isZipCodeRestricted(ctx, prodId)) ? (
                <p className='mt-1'>Post code is restricted for some products</p>
            ) : (
                <></>
            )}
            {name?.toLowerCase() === 'zipcode' && ctx && getDelayedDelMsgForEnteredZipCode(ctx, value) ? (
                <p className='delivery-note'>{` ${getDelayedDelMsgForEnteredZipCode(ctx, value)}`}</p>
            ) : (
                <></>
            )}
        </>
    );
};
/* VSI Customization - END */
export default AddressInputFunctionComponent;
